<template>
	<article class="aircraft-card" @click="onClick(aircraft.cheapestCraft.id)">
		<div class="aircraft-card__container">
			<!-- Images gallery -->
			<div
				class="aircraft-card__images swiper-container aspect-ratio"
				style="--aspect-ratio:2/3;"
				:data-swiper-index="swiperIndex"
			>
				<div class="swiper-wrapper">
					<img
						v-for="(image, slideIndex) in images"
						:key="slideIndex"
						:src="image"
						class="aircraft-card__image swiper-slide"
					/>
				</div>
				<div class="swiper-pagination" :data-swiper-index="swiperIndex"></div>
				<div class="swiper-button-prev" :data-swiper-index="swiperIndex"></div>
				<div class="swiper-button-next" :data-swiper-index="swiperIndex"></div>
			</div>

			<!-- Header -->
			<header class="aircraft-card__header">
				<!-- Title -->
				<div class="aircraft-card__heading">
					<h1 class="aircraft-card__title type--r type--sans type--b">{{ modelNo }}</h1>
					<p class="aircraft-card__subtitle type--s type--sans">{{ cost }}</p>
					<!-- <p class="aircraft-card__per-person type--s type--serif">Home base: {{ base }}</p> -->
				</div>

				<!-- Stats -->
				<div class="aircraft-card__stats type--sans type--s">
					<div class="aircraft-card__stat">
						<span>{{ maxPassengers }}</span>
						<img src="@/assets/icons_person.svg" class="aircraft-card__stat-icon" />
					</div>
					<div class="aircraft-card__stat">
						<span>{{ journeyHours }}h {{ journeyMinutes }}m</span>
					</div>
				</div>
			</header>

			<!-- Description -->
			<p class="aircraft-card__body type--serif">{{ description }}</p>
		</div>
	</article>
</template>

<script>
import Swiper from "swiper"
import toCurrency from "@/utils/toCurrency"

export default {
	data() {
		return {
			base: '',
			modelNo: '',
			cost: '',
			maxPassengers: '',
			journeyHours: '',
			journeyMinutes: '',
			description: '',
			images: []
		}
	},

	name: "AircraftCard",

	props: {
		aircraft: Object,
		swiperIndex: {
			type: Number,
			required: true
		},
	},

	created() {
		this.setImages(this.aircraft)
		this.setText(this.aircraft)
	},

	mounted() {
		/* eslint-disable no-unused-vars */
		let swiper = new Swiper(`.aircraft-card__images[data-swiper-index="${this.swiperIndex}"]`, {
			loop: false,
			watchOverflow: true,
			navigation: {
				nextEl: `.swiper-button-next[data-swiper-index="${this.swiperIndex}"]`,
				prevEl: `.swiper-button-prev[data-swiper-index="${this.swiperIndex}"]`,
			},
			pagination: {
				el: `.swiper-pagination[data-swiper-index="${this.swiperIndex}"]`,
			},
		})
		/* eslint-enable no-unused-vars */
	},

	methods: {
		/**
		 * Set the images array to multiple (if available) or single
		 */
		setImages(aircraft) {
			if (aircraft.images.length === 0)
				this.images = [`${aircraft.craft_image}`]

			if (aircraft.images.length > 0)
				this.images = aircraft.images.map(({file_path}) => file_path)
		},

		/**
		 * Set all text fields
		 */
		setText(aircraft) {
			this.base = aircraft.cheapestCraft.location_name
			this.modelNo = aircraft.cheapestCraft.aircraft_model.model_no
			this.cost = toCurrency(aircraft.aggregated_total_cost_currency, aircraft.aggregated_total_cost)
			this.journeyHours = aircraft.cheapestCraft.processedFlyts[0].allStepsCostsAndTimings.customerJourneyDetails.hours
			this.journeyMinutes = aircraft.cheapestCraft.processedFlyts[0].allStepsCostsAndTimings.customerJourneyDetails.minutes
			this.maxPassengers = aircraft.maxPassengers
			this.description = aircraft.cheapestCraft.aircraft_model.description
		},

		/**
		 * When card clicked, emit selected event with aircraft ID
		 */
		onClick(id) {
			this.$emit("selected", id)
		},
	},
}
</script>

<style lang="scss" scoped>
.aircraft-card {
	cursor: pointer;
	margin: 1rem;
	position: relative;
	z-index: 0;
	// Selected inactive
	&:after {
		background: $colour-teal;
		border-radius: 0.5rem;
		bottom: -0.25rem;
		content: "";
		left: -0.25rem;
		opacity: 0;
		position: absolute;
		right: -0.25rem;
		top: -0.25rem;
		transition: opacity 150ms;
		z-index: -1;
	}
	// Selected active
	&.is-selected:after {
		opacity: 1;
	}
}

// All card content
.aircraft-card__container {
	background-color: $colour-stone;
	border-radius: 0.25rem;
	overflow: hidden;
}

// Image carousel
.aircraft-card__images {
	// Position all swiper UI relative to container
	position: relative;
}

// Pagination
.swiper-pagination {
	bottom: 0.75rem;
	display: flex;
	flex-direction: row;
	left: 50%;
	position: absolute;
	transform: translateX(-50%);
	z-index: 1;
	// Hide pagination if only one slide
	&.swiper-pagination-lock {
		display: none;
	}
	// Inactive bullets
	/deep/ .swiper-pagination-bullet {
		background: #fff;
		border-radius: 50%;
		display: inline-block;
		height: 0.375rem;
		margin: 0 0.25rem;
		opacity: 0.75;
		transition: transform 150ms, opacity 150ms;
		width: 0.375rem;
	}
	// Active bullets
	/deep/ .swiper-pagination-bullet-active {
		opacity: 1;
		transform: scale(1.25);
	}
}

// Navigation buttons
.swiper-button-prev,
.swiper-button-next {
	display: block;
	height: 2.5rem;
	opacity: 0;
	position: absolute;
	top: 50%;
	transition: transform 150ms, opacity 150ms;
	width: 2.5rem;
	z-index: 1;
	// Hide if only one slide
	&.swiper-button-lock {
		display: none;
	}
	// Button elements
	&:before,
	&:after {
		content: "";
		display: block;
		position: absolute;
		transition: opacity 150ms;
	}
	// Button background
	&:before {
		background: #fff;
		border-radius: 50%;
		height: 100%;
		opacity: 0;
		width: 100%;
	}
	// Button arrow
	&:after {
		background: linear-gradient(180deg, white 25%, transparent 25%),
			linear-gradient(90deg, white 25%, transparent 25%);
		height: 1rem;
		top: 50%;
		width: 1rem;
	}
	// Button hover background
	&:hover:before {
		opacity: 0.375;
	}
}

// Prev button positioning
.swiper-button-prev {
	left: 0.5rem;
	transform: translate(-50%, -50%);
	// Prev button arrow
	&:after {
		left: 55%;
		transform: translate(-50%, -50%) rotate(-45deg);
	}
}

// next button positioning
.swiper-button-next {
	right: 0.5rem;
	transform: translate(50%, -50%);
	// Next button arrow
	&:after {
		left: 45%;
		transform: translate(-50%, -50%) rotate(135deg);
	}
}

// Make button arrows visible on swiper hover
.aircraft-card__images:hover {
	.swiper-button-prev,
	.swiper-button-next {
		opacity: 1;
		transform: translate(0, -50%);
	}
}

.swiper-wrapper {
	position: absolute;
}

.aircraft-card__image {
	display: block;
	height: 100%;
	object-fit: cover;
	object-position: 50% 50%;
	width: 100%;
}

.aircraft-card__header {
	align-items: baseline;
	display: flex;
	flex-direction: row;
	padding: 0.75rem 1rem;
}

.aircraft-card__stats {
	align-items: flex-start;
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	justify-content: flex-end;
	margin-left: auto;
}

.aircraft-card__stat {
	background: #fff;
	border-radius: 0.25rem;
	box-shadow: 0 0.05rem 0.1rem rgba(0, 0, 0, 0.05);
	display: flex;
	flex-direction: row;
	margin: 0 0 0.25rem 0.25rem;
	padding: 0.25rem 0.5rem 0.125rem;
}

.aircraft-card__stat-icon {
	padding-left: 0.125em;
}

.aircraft-card__per-person {
	color: $colour-pencil--dark;
}

.aircraft-card__body {
	color: $colour-pencil--dark;
	line-height: 1.2;
	padding: 0 1rem 1.25rem;
}
</style>
